<template>
    <div class="q-pa-md" style="overflow:auto; height:calc(100vh - 50px);">
        <div class="text-h3">Syntaxe pour l'écriture de partitions de djembé</div>
        <div class="text-h4">Généralités</div>
        <p>
            Les partitions s'écrivent sous une forme particulière de wiki, c'est à dire que c'est le travail de l'auteur 
            d'une partition de spécifier le format à apporter. Ces formats sont définis par des balises. Les balises sont des 
            suites de caractères pré-définies. A titre d'exemple, dans le texte suivant
            <br />
            "Abondon est un <strong>rythme</strong> très ancien du peuple <em>Bolé</em> de Côte d'Ivoire."
            <br />
            Le mot 'rythme' est rendu en gras, tandis que le mot 'Bolé' est rendu en italiques. Dans cet outil, pour préciser
            ce formatage spécial, la phrase devra s'écrire :
            <br />
            "Abondon est un <span style="color:red">{bo}</span>rythme<span style="color:red;">{/bo}</span> très ancien 
            du peuple <span style="color:red">{it}</span>Bolé<span style="color:red;">{/it}</span> de Côte d'Ivoire."
            <br />
            Dans cet exemple, les balises ont été colorées en rouge pour attirer l'attention. On voit bien que le mot 'rythme' 
            est entouré des balises <span style="color:red">{bo}</span> et <span style="color:red">{/bo}</span> ('bo', pour 
            'bold', qui signifie gras en anglais), tandis que le mot 'Bolé' est entouré des balises <span style="color:red">{it}</span>
            et <span style="color:red">{/it}</span>, 'it' pour 'italiques'.
        </p>
        <p>
            De cet exemple, il se dégage déjà deux règles essentielles:
            <ul>
                <li>Les balises sont toujours entourées d'accolades, ce qui permet au programme de les reconnaître.</li>
                <li>La balise de fin comporte le même texte que la balise de début, mais elle est préfixée par le signe divisé (/).
                    <br />
                    Donc, tout texte formatté commence par {...} et termine par {/...}, où les trois point sont à remplacer par
                    le format désiré. Cette notion est essentielle, parce qu'elle constitue le coeur du formatage : il est donc 
                    indispensable de la comprendre.
                </li>
            </ul>
        </p>
        <p>
            Dernière règle essentielle: les balises ne peuvent pas se chevaucher : elle doivent impérativement être englobantes.
            <br />
            La syntaxe suivante est invalide: {bo}gras seulement{it}, gras et italique, {/bo}italique seulement{/it}<br />
            Pour obtenir le bon résultat, il faut écrire {bo}gras seulement{it}, gras et italique, {/it}{/bo}{it}italique seulement{/it}.
        </p>
        <p>
            Le reste de ce document décrit les différentes balises et leur effet.
        </p>
        <div class="text-h4">Les balises de formatage du texte</div>
        <table class="table-parameter">
            <tr> <th>Balise</th> <th>Effet</th> <th>Remarque</th> </tr>
            <tr> <td>{bo}...{/bo}</td> <td>Affiche le texte en gras</td> <td>texte {bo}en gras{/bo} = texte <strong>en gras</strong></td> </tr>
            <tr> <td>{it}...{/it}</td> <td>Affiche le texte en italiques</td> <td>texte {it}en italiques{/it} = texte <em>en italiques</em></td> </tr>
            <tr> <td>{pg}...{/pg}</td> <td>Englobe un parapgraphe</td> <td>Le paragraphe commence et termine par un retour à la ligne.</td> </tr>
            <tr> <td>{pf}...{/pf}</td> <td>Paragraphe préformaté</td> <td>Dans ce paragraphe, tous les espaces et retour à la ligne seront soigneusement conservés et affichés. Ce type de paragraphe est à utiliser lorsque le formatage par défaut ne convient pas ou montre ses limites, comme dans le cas des douns en batteries, non encore supportés.</td> </tr>
            <tr> <td>{np}...{/np}</td> <td>Paragraphe invisible</td> <td>Cette balise s'utilise pour laisser des notes de rédaction qui ne seront visibles que dans la fenêtre d'édition.</td> </tr>
            <tr> <td>{t1}...{/t1}</td> <td>Titre de niveau "1"</td> <td>Le niveau "1" est le niveau le plus haut, et donc le plus mis en évidence dans le texte. Ex: {t1}Mon titre 1{/t1}</td> </tr>
            <tr> <td>{t2}...{/t2}</td> <td>Titre de niveau "2"</td> <td></td> </tr>
            <tr> <td>{t3}...{/t3}</td> <td>Titre de niveau "3"</td> <td></td> </tr>
            <tr> <td>{t4}...{/t4}</td> <td>Titre de niveau "4"</td> <td></td> </tr>
            <tr> <td>{t5}...{/t5}</td> <td>Titre de niveau "5"</td> <td></td> </tr>
            <tr> <td>{t6}...{/t6}</td> <td>Titre de niveau "6"</td> <td></td> </tr>
            <tr> <td>{cf=xxx}...{/cf}</td> <td>Couleur de caractère ; xxx définit la couleur (note: les couleur doivent être exprimées en anglais)</td> <td>Ex: ceci {cf=red} est en rouge{/cf} = ceci <span style="color:red;"> est en rouge</span></td> </tr>
            <tr> <td>{cb=xxx}...{/cb}</td> <td>Couleur de fond ; xxx définit la couleur (note: les couleur doivent être exprimées en anglais)</td> <td>Ex: ceci {cb=red} est sur fond rouge{/cb} = ceci <span style="background-color:red;"> est sur fond rouge</span></td> </tr>
            <tr> <td>{ry}...{/ry}</td> <td>Balises de définition de rythme</td> <td>Voir ci-dessous pour plus de détails.</td> </tr>
        </table>
        
        <div class="text-h4">Trois balises spéciales</div>
        <p>
            Trois balises spéciales permettent d'aérer le texte en y plaçant soit un retour à la ligne, soit une 
            ligne horizontale pour séparer deux parties, soit un espacement.
        </p>
        <p>
            <strong>Note</strong>: Comme ces trois balises ne contiennent pas de texte à proprement parler, il 
            n'est pas nécessaire de les fermer.
        </p>

        <table class="table-parameter">
            <tr> <th>Balise</th> <th>Effet</th> <th>Remarque</th> </tr>
            <tr> <td>{br}</td> <td>Force un retour à la ligne</td> <td></td> </tr>
            <tr> <td>{hr}</td> <td>Affiche une fine ligne horizontale, en guise de séparateur</td> <td></td> </tr>
            <tr> <td>{sp}</td> <td>Force un espacement d'un caractère</td> <td></td> </tr>
            <tr> <td>{sp=xxx}</td> <td>Force un espacement d'une taille égale à xxx caractères</td> <td>A utiliser pour spécifier des espacements plus grands.</td> </tr>
        </table>

        <div class="text-h4">Les balises de tableaux</div class="text-h4">
        <p>
            Les tableaux sont utilisés principalement pour faciliter la mise en page et les alignements. En effet,
            un tableau se compose de lignes et de colonnes, toutes alignées.
        </p>
        <table class="table-parameter">
            <tr> <th>Balise</th> <th>Effet</th> <th>Remarque</th> </tr>
            <tr> <td>{tb}...{/tb}</td> <td>Déclare le début et la fin d'un tableau</td> <td>Pour des questions de lisibilité de la source, on écrit généralement la balise ouvrante seule sur une ligne (au début!), et la balise fermante seule sur une ligne aussi.</td> </tr>
            <tr> <td>{tr}...{/tr}</td> <td>Déclare le début et la fin d'une ligne dans le tableau.</td> <td></td> </tr>
            <tr> <td>{th}...{/th}</td> <td>Englobe une cellule de titre</td> <td></td> </tr>
            <tr> <td>{td}...{/td}</td> <td>Englobe une cellule standard</td> <td></td> </tr>
        </table>
        <p>
            <strong>Attention:</strong> comme la table est utilisée pour faciliter les alignements, la table elle-même n'est pas visible!
        </p>

        <div class="text-h4">Notation des rythmes</div>
        <p>
            Les rythmes sont définis par une paire de balises {ry}...{/ry}, où le rythme à proprement parlé est inclus dans les balises.
            <br />
            Au sein de ces balises, les caractères correspondent à des symboles représentant des notes ou des annotations dans la partition. 
            Ces caractères spéciaux sont décrits ci-dessous.
            <br />
            <strong>Notes:</strong><br />
            1) Attention aux majuscules et minuscules : elles n'ont pas la même signification!
            <br />
            2) Au sein de ces balises, aucun formattage n'est possible (gras, italique, couleurs, etc)
        </p>

        <div class="text-h4">Les caractères musicaux au sein des balises {ry}...{/ry}</div>

        <div class="text-h5">Les caractères généraux de mise en forme</div>
        <table class="table-parameter">
            <tr> <th>caractères</th> <th>Effet</th> <th>Remarque</th> </tr>
            <tr> <td>3 et 4</td> <td>Définissent le nombre de subdivisions dans le temp (4=binaire, 3=ternaire) et disposent une barre horizontale pour regrouper ces subdivisions.</td> 
                <td>Exemple: {ry}3,,,{/ry} ou {ry}4....{/ry} apparait comme <span class="p-music">3,,,</span> et <span class="p-music">4....</span></td> </tr>
            <tr> <td>, et .</td> <td>Indiquent les subdivisions vides. La virgule affiche un trait vertical, le point n'affiche qu'un point.</td> <td>(Voir exemple ci-dessus)</td> </tr>
            <tr> <td>[ et ]</td> <td>Les crochets indiquent une séquence qui ne se répète pas</td> 
                <td>{ry}[ 4b,tt, 4,,s, ]{/ry} apparait comme &nbsp;&nbsp;<span class="p-music">[4b,tt, 4,,s,]</span></td> </tr>
            <tr> <td>: et ;</td> <td>Les : et ; entourent une wséquence qui se répète en boucle</td> 
                <td>{ry}: 4b,tt, 4,,s, ;{/ry} apparait comme &nbsp;&nbsp;<span class="p-music">:4b,tt, 4,,s,;</span></td> </tr>
            <tr> <td>a</td> <td>Anacrouse. Elle indique la première note jouée, à synchoniser avec la fin de l'appel ou du break. A placer avant la note concernée.</td> 
                <td>{ry}4b,tt, 4,,as,{/ry} apparait comme <span class="p-music">4b,tt, 4,,as,</span></td> </tr>
            <tr> <td>d, g, r, l</td> <td>Indication de la main qui joue la note (G(auche), D(roite), L(eft), R(ight). A placer avant la note concernée.</td> 
                <td>{ry}3dbg,dt 3gtd,gs{/ry} apparait comme <span class="p-music">3dbg,dt 3gtd,gs</span></td> </tr>
        </table>

        <div class="text-h5">Les sons propres au djembé</div>
        <table class="table-parameter">
            <tr> <th>Code</th> <th>Effet</th> <th>Remarque</th> </tr>
            <tr> <td>s</td> <td>Note claquée, aussi appelée 'TA', ou 'slap' en anglais</td> <td>{ry}4s,,,{/ry} = <span class="p-music">4s,,,</span></td> </tr>
            <tr> <td>t</td> <td>Note tonique, aussi appelée 'TO'</td> <td>{ry}4t,,,{/ry} = <span class="p-music">4t,,,</span></td> </tr>
            <tr> <td>b</td> <td>Basse</td> <td>{ry}4b,,,{/ry} = <span class="p-music">4b,,,</span></td> </tr>
            <tr> <td>f</td> <td>Fla. C'est le double coup en claqués, typique de l'appel binaire</td> <td>{ry}4f,,,{/ry} = <span class="p-music">4f,,,</span></td> </tr>
            <tr> <td>n</td> <td>Note précédent la note: elle permet de composer un fla différent du double claqué</td>
                <td>Ex: {ry}4nb,,,{/ry} apparait comme <span class="p-music">4nb,,,</span></td> </tr>
            <tr> <td>q</td> <td>Basse précédent la note: elle permet de composer un fla différent du double claqué</td>
                <td>Ex: {ry}4qt,,,{/ry} apparait comme <span class="p-music">4qt,,</span></td> </tr>
            <tr> <td>u</td> <td>Claqué précédent la note: elle permet de composer un fla différent du double claqué</td>
                <td>Ex: {ry}4ub,,,{/ry} apparait comme <span class="p-music">4ub,,</span></td> </tr>
            <tr> <td>w</td> <td>Tonique précédent la note: elle permet de composer un fla différent du double claqué</td>
                <td>Ex: {ry}4wb,,,{/ry} apparait comme <span class="p-music">4wb,,</span></td> </tr>
            <tr> <td>_</td> <td>Note étouffée. Ce caractère se place avant la note concernée.</td> 
                <td>Ex: {ry}4_t,t,{/ry} apparait comme <span class="p-music">4_t,t,</span></td> </tr>
            <tr> <td>=</td> <td>permet d'ajouter une note intermédiaire ; ce caractère n'a de sens que s'il est utilisé conjointement avec 'i' ou 'j'</td>
                <td>Ex: {ry}4t=t,,{/ry} apparait comme <span class="p-music">4t=t,,</span></td> </tr>
            <tr> <td>i</td> <td>Note tonique intermédiaire</td>
                <td>Ex: {ry}4t=it,,{/ry} apparait comme <span class="p-music">4t=it,,</span></td> </tr>
            <tr> <td>j</td> <td>Note claquée intermédiaire</td>
                <td>Ex: {ry}4s=js,,{/ry} apparait comme <span class="p-music">4s=js,,</span></td> </tr>
            <tr> <td>c</td> <td>Claquement de mains</td>
                <td>Ex: {ry}4c,c,{/ry} apparait comme <span class="p-music">4c,c,</span></td> </tr>
        </table>

        <div class="text-h5">Les sons propres aux douns</div>
        <table class="table-parameter">
            <tr> <th>Code</th> <th>Effet</th> <th>Remarque</th> </tr>
            <tr> <td>x</td> <td>Coup de cloche</td>
                <td>Une croix apparait sur la barre du temps: {ry}4x,,x,,{/ry} apparait comme <span class="p-music">4x,,x,,</span></td> </tr>
            <tr> <td>t</td> <td>Coup normal sur la peau</td>
                <td>Ex: {ry}4t,,,{/ry} apparait comme <span class="p-music">4t,,,</span></td> </tr>
            <tr> <td>T</td> <td>Coup faible sur la peau</td>
                <td>Ex: {ry}4T,t,{/ry} apparait comme <span class="p-music">4T,t,</span>(un coup faible et un coup fort, pour illustrer la différence)</td> </tr>
            <tr> <td>_</td> <td>Coup étouffé sur la peau</td>
                <td>Ex: {ry}4_t,,,{/ry} apparait comme <span class="p-music">4_t,,,</span></td> </tr>
            <tr> <td>B</td> <td>Coup sur le bois ou le cerclage</td>
                <td>Ex: {ry}4B,,,{/ry} apparait comme <span class="p-music">4B,,,</span></td> </tr>
            <tr> <td colspan="3"><strong>ATTENTION</strong> : 
                Les codes des dunums sont soit visuels, soit auditifs!<br />
                Pour une représentation visuelle, il convient de postfixer la balise de cette façon: {ry=nosound}...{/ry}.<br/>
                En revanche, pour une représentation sonore, il faut postfixer la balise comme suit: {ry=nodisplay}...{/ry}.<br />
                Les codes suivants ne s'appliquent que pour la représentation auditive des dunums:
            </td></tr>
            <tr> <td>K</td> <td>Coup sur la peau du kenkeni</td>
                <td>Ex: {ry}4K,,,{/ry}</td> </tr>
            <tr> <td>S</td> <td>Coup sur la peau du sangban</td>
                <td>Ex: {ry}4S,,,{/ry}</td> </tr>
            <tr> <td>D</td> <td>Coup sur la peau du dunumba</td>
                <td>Ex: {ry}4D,,,{/ry}</td> </tr>
            <tr> <td>x</td> <td>Coup de cloche</td>
                <td>La cloche ne "consomme" pas de temps. Ex: {ry}4xD,,,{/ry} fera sonner en même temps le dunumba et la cloche.</td> </tr>
            <tr> <td>-</td> <td>Le signe "moins" (-) qui précède le dunum à jouer spécifie que le temps n'avance pas et que le coup suivant sera joué au même instant (en réalité, une faction de seconde plus tôt, pour être audible). </td>
                <td>Ex: {ry}4-KD,,,{/ry}</td> jouera en même temps le kenkeni (-K) et le dunumba (D).</tr>
            <tr> <td colspan="3">Les sons "coup sur le cerclage", "coup faible" et "coup étouffé" ne sont pas encore supportés.</td></tr>
        </table>

        <div class="text-h5">Quelques exemples <em>classiques</em></div>
        <table class="table-parameter">
            <tr> <th>Code</th> <th>Effet</th> <th>Remarque</th> </tr>
            <tr>
                <td>{ry}[ 4wt.tt 4.t.t 4t.ss 4s.. ]{/ry}</td>
                <td><span class="p-music">[ 4wt.tt 4.t.t 4t.ss 4s.. ]</span></td>
                <td>C'est l'appel binaire le plus courant</td>
            </tr>
            <tr>
                <td>{ry} : 4b.tt 4b.s. ; {/ry}</td>
                <td><span class="p-music"> : 4b.tt 4b.s. ; </span></td>
                <td>Le premier accompagnement de Moribayassa</td>
            </tr>
            <tr>
                <td>{ry}: 3dsg,dt 3gtd,g, 3asg,dt 3gtd,g, ;{/ry}</td>
                <td><span class="p-music">: 3dsg,dt 3gtd,g, 3asg,dt 3gtd,g, ;</span></td>
                <td>Le deuxième accompagnement <em>passe-partout</em> en ternaire</td>
            </tr>
            <tr>
                <td>{ry}: 4tt,s 4b,s, 4b,,s 4b,s, 4tt,s 4b,s, 4tt,s 4b,s, ;{/ry}</td>
                <td><span class="p-music">: 4tt,s 4b,s, 4b,,s 4b,s, 4tt,s 4b,s, 4tt,s 4b,s, ;</span></td>
                <td>Une phrase solo de Famoudou Konaté, sur <em>Ké bendo</em></td>
            </tr>
            <tr>
                <td>{ry}: 4xt,,xt 4xt,x,, 4xt,,xt, 4xt,x,, ;{/ry}</td>
                <td><span class="p-music">: 4xt,,xt 4xt,x,, 4xt,,xt, 4xt,x,, ;</span></td>
                <td>Le jeu du dunumba, sur <em>Makru</em></td>
            </tr>
        </table>
    </div>
</template>

<script>
export default {
    name: "WriterSyntaxHelp",
}
</script>

<style>
    .table-parameter {
        border:1px solid Gray;
        border-spacing : 0;
        border-collapse : collapse;
    }
    .table-parameter th {
        text-align: left;
        background-color: ghostwhite;
    }
    .table-parameter td, .table-parameter th {
        border: 1px solid Gray;
        padding: .3em;
    }
    @font-face {
        font-family: djembe;
        src: url(/Djembe.ttf);
    }
</style>
